/**
 * QuoteAssistant — bouton "? Aide IA" cliquable à côté d'un champ du formulaire.
 *
 * Au clic, ouvre une mini-bulle d'aide propulsée par Gemini qui :
 *  • Explique le champ en 1-3 phrases
 *  • Donne une fourchette typique selon le contexte (audience, énergie…)
 *  • Pointe vers la facture si le visiteur ne sait pas où trouver l'info
 *
 * Usage :
 *   <QuoteAssistant
 *     fieldName="annual_kwh"
 *     fieldLabel="Consommation annuelle (kWh)"
 *     currentFormData={formState}
 *     defaultQuestion="Où trouver ma conso annuelle ?"
 *   />
 */
import { useState } from "react";
import { Sparkles, X, Loader2, HelpCircle } from "lucide-react";
import api from "@/lib/api";

export default function QuoteAssistant({
    fieldName,
    fieldLabel,
    currentFormData,
    defaultQuestion,
    placement = "bottom-right", // "bottom-right" | "top-left"
}) {
    const [open, setOpen] = useState(false);
    const [question, setQuestion] = useState(defaultQuestion || "");
    const [answer, setAnswer] = useState(null);
    const [loading, setLoading] = useState(false);

    const ask = async (q) => {
        const text = (q || question || "").trim();
        if (!text) return;
        setLoading(true);
        setAnswer(null);
        try {
            const { data } = await api.post("/chatbot/quote-assist", {
                field_name: fieldName,
                field_label: fieldLabel || fieldName,
                user_question: text,
                current_form_data: currentFormData,
            });
            setAnswer(data.answer);
        } catch (e) {
            setAnswer("Désolée, l'assistant IA est temporairement indisponible. Reportez-vous à votre dernière facture ou contactez-nous.");
        } finally {
            setLoading(false);
        }
    };

    const presets = [
        defaultQuestion || "Où trouver cette information ?",
        "Quelle est une valeur typique ?",
        "Cette donnée est-elle obligatoire ?",
    ];

    return (
        <div className="relative inline-block">
            {/* Trigger : petit bouton "? Aide IA" */}
            <button
                type="button"
                onClick={() => {
                    setOpen(!open);
                    if (!open && !answer) ask(defaultQuestion);
                }}
                title="Aide IA pour ce champ"
                className="inline-flex items-center gap-1 text-xs text-[#0A3B2A]/70 hover:text-[#0A3B2A] font-medium transition-colors"
            >
                <Sparkles className="h-3.5 w-3.5" />
                Aide IA
            </button>

            {/* Bulle d'aide */}
            {open && (
                <div
                    className={`absolute z-30 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl ring-1 ring-[#0A3B2A]/10 p-4 ${
                        placement === "top-left"
                            ? "right-0 bottom-full mb-2"
                            : "left-0 top-full mt-2"
                    }`}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-full bg-[#0A3B2A] flex items-center justify-center text-white">
                                <Sparkles className="h-3.5 w-3.5" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-[#0A3B2A]">Emma — Assistante IA</p>
                                <p className="text-[10px] text-gray-500">Aide sur « {fieldLabel || fieldName} »</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-gray-400 hover:text-gray-700"
                            aria-label="Fermer"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Réponse */}
                    <div className="mt-2 min-h-[60px] text-sm text-gray-700 leading-relaxed">
                        {loading ? (
                            <div className="flex items-center gap-2 text-gray-400 py-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Emma réfléchit…
                            </div>
                        ) : answer ? (
                            <p className="whitespace-pre-line">{answer}</p>
                        ) : (
                            <p className="text-gray-400 italic">Posez votre question ci-dessous.</p>
                        )}
                    </div>

                    {/* Preset quick buttons */}
                    {!loading && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {presets.map((p, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => ask(p)}
                                    className="text-[11px] px-2.5 py-1 rounded-full bg-[#0A3B2A]/5 hover:bg-[#0A3B2A] hover:text-white text-[#0A3B2A] transition-colors"
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Question libre */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            ask(question);
                        }}
                        className="mt-3 flex items-center gap-2"
                    >
                        <input
                            type="text"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Posez une question…"
                            className="flex-1 text-xs bg-gray-50 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A3B2A]/30"
                        />
                        <button
                            type="submit"
                            disabled={loading || !question.trim()}
                            className="h-8 w-8 rounded-full bg-[#0A3B2A] text-white flex items-center justify-center hover:bg-[#072719] disabled:opacity-30"
                            aria-label="Demander"
                        >
                            <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}
